/**
 * Layout Component
 * 
 * Main application layout with header navigation and footer.
 * Wraps all pages and provides consistent UI across the application.
 * 
 * @usage Wraps all routes in App.tsx
 * 
 * Features:
 * - Dynamic navigation based on user type (employee/employer/guest)
 * - Authentication modal integration
 * - Mobile responsive menu
 * - User profile dropdown
 * - Mode switching (employee ↔ employer)
 * 
 * Navigation Items:
 * - Employees: Nauja paieška, Išsaugoti paieškos, Prenumeratos, Pažymėti skelbimai, Kandidatavimas, Įkelti CV
 * - Employers: Different menu (candidate search, post jobs, etc.)
 * - Guests: Login/Register options
 * 
 * Note:
 * - Uses global Layout.scss for styling
 * - Should NOT contain component-specific styles (use CSS Modules in components)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  User, 
  Building2, 
  Lock, 
  Search, 
  Bookmark, 
  Mail, 
  Star, 
  Upload,
  LogOut,
  Settings,
  Briefcase,
  Plus,
  FileText,
  Menu,
  X,
  CreditCard,
  Bell,
  Palette
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import FreeTrialWelcomeModal from '../Common/FreeTrialWelcomeModal';
import './Layout.scss';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Application State - Get mode from URL or default to employee
  const [searchParams, setSearchParams] = useSearchParams();
  
  const getCurrentModeFromUrl = (): 'employee' | 'employer' => {
    const mode = searchParams.get('mode');
    return (mode === 'employer') ? 'employer' : 'employee';
  };
  
  const [currentPath, setCurrentPath] = useState<'employee' | 'employer'>(getCurrentModeFromUrl());
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [navCounts, setNavCounts] = useState<{ savedSearches: number; subscriptions: number; savedJobs: number; applications: number }>({ savedSearches: 0, subscriptions: 0, savedJobs: 0, applications: 0 });
  // Unread Freelance Gig messages — surfaced as a small badge on the
  // "Freelance" nav entry. Refreshed on every route change so it stays
  // current without dedicated polling.
  const [freelanceUnread, setFreelanceUnread] = useState(0);
  // Interview-invitation badge ("Pakviesti pokalbiui" feature). For candidates
  // it counts pending invites + unread messages; for employers it counts
  // unread messages from the candidate side.
  const [interviewUnread, setInterviewUnread] = useState(0);

  // Free-trial welcome modal — shown once per employer while trial is active.
  // The "seen" flag lives server-side (not localStorage) so it stays dismissed
  // across browsers and cache clears.
  const [showTrialModal, setShowTrialModal] = useState(false);
  useEffect(() => {
    if (!user || user.user_type !== 'employer') return;
    const trialEnd = new Date('2026-10-01');
    if (new Date() > trialEnd) return; // promotion period over
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    fetch(`${API_URL}/profile/free-trial-welcomed`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.success || data.data?.welcomed) return;
        timer = setTimeout(() => setShowTrialModal(true), 2000);
      })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [user]);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('auth_token');

    if (isAuthenticated && token && (user?.user_type === 'employee' || user?.user_type === 'job_seeker')) {
      const headers = { Authorization: `Bearer ${token}` };
      Promise.allSettled([
        fetch(`${API_URL}/saved-searches/my`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/saved-jobs/my/count`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/job-applications/my`, { headers }).then(r => r.ok ? r.json() : null),
      ]).then(([searchesRes, jobsRes, appsRes]) => {
        const searchData = searchesRes.status === 'fulfilled' ? searchesRes.value : null;
        const searches = searchData?.data?.length ?? searchData?.count ?? 0;
        const subs = Array.isArray(searchData?.data)
          ? searchData.data.filter((s: any) => s.is_subscribed).length : 0;
        const jobs = jobsRes.status === 'fulfilled' && jobsRes.value?.count != null ? jobsRes.value.count : 0;
        const apps = appsRes.status === 'fulfilled' && appsRes.value?.data ? appsRes.value.data.length : 0;
        setNavCounts({ savedSearches: searches, subscriptions: subs, savedJobs: jobs, applications: apps });
      });
    } else {
      setNavCounts({ savedSearches: 0, subscriptions: 0, savedJobs: 0, applications: 0 });
    }
  }, [isAuthenticated, user, location.pathname]);

  // Refresh the Freelance Gig unread badge for any authenticated user.
  // Kept in its own effect because it works for both employer and employee
  // and shouldn't depend on the employee-only counters above. Re-runs on
  // route change AND on a `freelance-unread-changed` custom event dispatched
  // by the thread page after marking messages as read.
  useEffect(() => {
    if (!isAuthenticated) {
      setFreelanceUnread(0);
      return;
    }
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    const fetchUnread = () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      fetch(`${API_URL}/freelance-gigs/unread-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((payload) => {
          const total = payload?.data?.total;
          setFreelanceUnread(typeof total === 'number' ? total : 0);
        })
        .catch(() => setFreelanceUnread(0));
    };

    fetchUnread();
    window.addEventListener('freelance-unread-changed', fetchUnread);
    return () => window.removeEventListener('freelance-unread-changed', fetchUnread);
  }, [isAuthenticated, user, location.pathname]);

  // Interview-invitation unread badge. Same pattern as the freelance badge
  // above; the backend endpoint returns a single `total` for the current role.
  useEffect(() => {
    if (!isAuthenticated) {
      setInterviewUnread(0);
      return;
    }
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    const fetchUnread = () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      fetch(`${API_URL}/interview-invitations/unread-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((payload) => {
          const total = payload?.data?.total;
          setInterviewUnread(typeof total === 'number' ? total : 0);
        })
        .catch(() => setInterviewUnread(0));
    };

    fetchUnread();
    window.addEventListener('interview-invitations-changed', fetchUnread);
    return () =>
      window.removeEventListener('interview-invitations-changed', fetchUnread);
  }, [isAuthenticated, user, location.pathname]);

  // Handle mode switching with URL update
  const handleModeSwitch = useCallback((mode: 'employee' | 'employer') => {
    setCurrentPath(mode);
    
    // Update URL to reflect the mode
    const params = new URLSearchParams(searchParams);
    if (mode === 'employer') {
      params.set('mode', 'employer');
    } else {
      params.delete('mode'); // Default to employee, so no need for URL param
    }
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  // Guest audience toggle. On the login/register pages, stay on that page and
  // just switch the type/mode params so the form adapts to the audience.
  // Everywhere else, navigate to that audience's main search page so the
  // content actually changes (not just the sidebar): job seekers see the job
  // listing; employers see the candidate search.
  const handleAudienceToggle = useCallback((mode: 'employee' | 'employer') => {
    setCurrentPath(mode);
    const authPage = location.pathname === '/login' || location.pathname === '/register';
    if (authPage) {
      const params = new URLSearchParams(searchParams);
      params.set('type', mode);
      params.set('mode', mode);
      navigate(`${location.pathname}?${params.toString()}`);
    } else if (mode === 'employer') {
      navigate('/skelbimai?mode=employer');
    } else {
      navigate('/darbo-skelbimai');
    }
  }, [navigate, location.pathname, searchParams]);

  // Sync URL changes with local state
  React.useEffect(() => {
    const urlMode = getCurrentModeFromUrl();
    if (urlMode !== currentPath) {
      setCurrentPath(urlMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentPath]);

  // Close mobile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMobileMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.nav-left') && !target.closest('.mobile-menu-toggle')) {
          setShowMobileMenu(false);
        }
      }
    };

    if (showMobileMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMobileMenu]);

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showUserMenu]);

  // Set default view for employers
  React.useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') {
      handleModeSwitch('employer');
    }
  }, [isAuthenticated, user, handleModeSwitch]);

  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else if (user?.first_name) {
      return user.first_name;
    } else if (user?.company_name) {
      return user.company_name;
    } else {
      return user?.email || 'Vartotojas';
    }
  };

  const handleLogout = useCallback(() => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  }, [logout, navigate]);

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="cvgidas-homepage">
      {/* TOP HEADER */}
      <div className="sticky-header-wrap">
      <header className={`top-header ${isAuthenticated ? 'logged-in' : ''}`}>
        <div className="container">
          <div className="header-left">
            <Link to="/" className="logo"><img src="/logo.svg" alt="CVgidas.lt" className="logo-img" /></Link>
          </div>

          <div className="header-center">
            <div className="nav-tabs">
              {!isAuthenticated ? (
                <>
                  <button
                    className={`nav-tab ${currentPath === 'employee' ? 'active' : ''}`}
                    onClick={() => handleAudienceToggle('employee')}
                  >
                    <User size={16} />
                    IEŠKANTIEMS DARBO
                  </button>
                  <button
                    className={`nav-tab ${currentPath === 'employer' ? 'active' : ''}`}
                    onClick={() => handleAudienceToggle('employer')}
                  >
                    <Building2 size={16} />
                    DARBDAVIAMS
                  </button>
                </>
              ) : user?.user_type === 'employee' || user?.user_type === 'job_seeker' ? (
                <button className="nav-tab active">
                  <User size={16} />
                  IEŠKANTIEMS DARBO
                </button>
              ) : user?.user_type === 'employer' ? (
                <button className="nav-tab active">
                  <Building2 size={16} />
                  DARBDAVIAMS
                </button>
              ) : (
                // Fallback for other user types or if user_type is not set
                <>
                  <button
                    className={`nav-tab ${currentPath === 'employee' ? 'active' : ''}`}
                    onClick={() => handleAudienceToggle('employee')}
                  >
                    <User size={16} />
                    IEŠKANTIEMS DARBO
                  </button>
                  <button
                    className={`nav-tab ${currentPath === 'employer' ? 'active' : ''}`}
                    onClick={() => handleAudienceToggle('employer')}
                  >
                    <Building2 size={16} />
                    DARBDAVIAMS
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="header-right">
            {isAuthenticated ? (
              <div className="header-right-content">
                <div className="user-menu" ref={userMenuRef}>
                  <button
                    className="user-btn"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                  >
                    <User size={16} />
                    {getUserDisplayName()}
                  </button>
                  {showUserMenu && (
                    <div className="user-dropdown">
                      <div className="user-info">
                        <div className="user-name">{getUserDisplayName()}</div>
                        <div className="user-email">{user?.email}</div>
                        <div className="user-type">
                          {user?.user_type === 'employee' || user?.user_type === 'job_seeker' ? 'Darbuotojas' : 'Darbdavys'}
                        </div>
                      </div>
                      <div className="menu-divider"></div>
                      <Link
                        to={user?.user_type === 'employee' || user?.user_type === 'job_seeker' ? '/profile/employee' : '/profile'}
                        className="menu-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Settings size={16} />
                        Profilis
                      </Link>
                      <button className="menu-item" onClick={handleLogout}>
                        <LogOut size={16} />
                        Atsijungti
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Link to={`/login?type=${currentPath}&mode=${currentPath}`} className="login-btn">
                <Lock size={14} />
                Prisijungti
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* SUB NAVIGATION */}
      <nav className="sub-nav">
        <div className="container">
          <button 
            className="mobile-menu-toggle"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            {showMobileMenu ? <X size={20} /> : <Menu size={20} />}
            Menu
          </button>
          <div className={`nav-left ${showMobileMenu ? 'mobile-menu-open' : ''}`}>
            {/* EMPLOYER MODE - both authenticated employers and non-authenticated in employer mode */}
            {(isAuthenticated && user?.user_type === 'employer') || (!isAuthenticated && currentPath === 'employer') ? (
              <>
                <Link
                  to="/?mode=employer"
                  className={`nav-item ${isActivePath('/') && searchParams.get('mode') === 'employer' ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Search size={16} />
                  Nauja paieška
                </Link>
                {isAuthenticated ? (
                  <Link
                    to="/employer/searches"
                    className={`nav-item ${isActivePath('/employer/searches') || isActivePath('/mano-paieskos') ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Bookmark size={16} />
                    Mano paieškos
                  </Link>
                ) : (
                  <Link
                    to="/login?type=employer&mode=employer&return=/employer/searches"
                    className="nav-item"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Bookmark size={16} />
                    Mano paieškos
                  </Link>
                )}
                {isAuthenticated ? (
                  <Link
                    to="/jobs?mode=employer"
                    className={`nav-item ${isActivePath('/jobs') ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Briefcase size={16} />
                    Skelbimai
                  </Link>
                ) : (
                  <Link
                    to="/login?type=employer&mode=employer&return=/jobs"
                    className="nav-item"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Briefcase size={16} />
                    Skelbimai
                  </Link>
                )}
                {isAuthenticated ? (
                  <Link
                    to="/employer/invitation-letters"
                    className={`nav-item ${isActivePath('/employer/invitation-letters') || isActivePath('/invitation-letters') || isActivePath('/atsakymu-laiskai') ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Mail size={16} />
                    Atsakymų laiškai
                  </Link>
                ) : (
                  <Link
                    to="/login?type=employer&mode=employer&return=/employer/invitation-letters"
                    className="nav-item"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Mail size={16} />
                    Atsakymų laiškai
                  </Link>
                )}
                {isAuthenticated ? (
                  <Link
                    to="/employer/services?mode=employer"
                    className={`nav-item ${isActivePath('/employer/services') ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <CreditCard size={16} />
                    Mano paslaugos
                  </Link>
                ) : (
                  <Link
                    to="/login?type=employer&mode=employer&return=/employer/services"
                    className="nav-item"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <CreditCard size={16} />
                    Mano paslaugos
                  </Link>
                )}
                {isAuthenticated && user?.is_recruitment_agency && (
                  <Link
                    to="/employer/clients"
                    className={`nav-item ${isActivePath('/employer/clients') ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Building2 size={16} />
                    Klientai
                  </Link>
                )}
                {isAuthenticated && (
                  <Link
                    to="/employer/pokalbiai"
                    className={`nav-item ${location.pathname.startsWith('/employer/pokalbiai') ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Mail size={16} />
                    Pokalbiai
                    {interviewUnread > 0 && <span className="nav-badge">{interviewUnread}</span>}
                  </Link>
                )}
                {/* Freelance kept last in the employer nav so the "Naujiena"
                    ribbon doesn't get visually crowded by the Pokalbiai badge. */}
                {isAuthenticated && (
                  <Link
                    to="/freelance/gigs?mode=employer"
                    className={`nav-item nav-item--with-ribbon ${location.pathname.startsWith('/freelance') ? 'active' : ''}`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Palette size={16} />
                    Freelance
                    {freelanceUnread > 0 && <span className="nav-badge">{freelanceUnread}</span>}
                    <span className="nav-ribbon">Naujiena</span>
                  </Link>
                )}
              </>
            ) : isAuthenticated ? (
              /* EMPLOYEE MODE - authenticated */
              <>
                <Link 
                  to="/" 
                  className={`nav-item ${isActivePath('/') && !searchParams.get('mode') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Search size={16} />
                  Nauja paieška
                </Link>
                <Link
                  to="/issaugotos-paieskos"
                  className={`nav-item ${isActivePath('/issaugotos-paieskos') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Bookmark size={16} />
                  Išsaugotos paieškos
                  {navCounts.savedSearches > 0 && <span className="nav-badge">{navCounts.savedSearches}</span>}
                </Link>
                <Link
                  to="/prenumeratos"
                  className={`nav-item ${isActivePath('/prenumeratos') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Bell size={16} />
                  Prenumeratos
                  {navCounts.subscriptions > 0 && <span className="nav-badge">{navCounts.subscriptions}</span>}
                </Link>
                <Link
                  to="/pazymeti-skelbimai"
                  className={`nav-item ${isActivePath('/pazymeti-skelbimai') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Star size={16} />
                  Pažymėti skelbimai
                  {navCounts.savedJobs > 0 && <span className="nav-badge">{navCounts.savedJobs}</span>}
                </Link>
                <Link 
                  to="/kandidatavimas" 
                  className={`nav-item ${isActivePath('/kandidatavimas') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Upload size={16} />
                  Kandidatavimas
                  {navCounts.applications > 0 && <span className="nav-badge">{navCounts.applications}</span>}
                </Link>
                <Link 
                  to="/mano-cv" 
                  className={`nav-item ${isActivePath('/mano-cv') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <FileText size={16} />
                  Mano CV
                </Link>
                <Link
                  to="/employee/services"
                  className={`nav-item ${isActivePath('/employee/services') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <CreditCard size={16} />
                  Paslaugos
                </Link>
                <Link
                  to="/pasiulymai"
                  className={`nav-item ${isActivePath('/pasiulymai') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Mail size={16} />
                  Pasiūlymai
                  {interviewUnread > 0 && <span className="nav-badge">{interviewUnread}</span>}
                </Link>
                <Link
                  to="/freelance/browse"
                  className={`nav-item nav-item--with-ribbon ${location.pathname.startsWith('/freelance') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Palette size={16} />
                  Freelance
                  {freelanceUnread > 0 && <span className="nav-badge">{freelanceUnread}</span>}
                  <span className="nav-ribbon">Naujiena</span>
                </Link>
              </>
            ) : (
              /* EMPLOYEE MODE - guest: same labels, account pages go to register */
              <>
                <Link 
                  to="/darbo-skelbimai" 
                  className={`nav-item ${isActivePath('/darbo-skelbimai') ? 'active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Search size={16} />
                  Nauja paieška
                </Link>
                <Link
                  to="/register?type=employee&mode=employee"
                  className="nav-item"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Bookmark size={16} />
                  Išsaugotos paieškos
                </Link>
                <Link
                  to="/register?type=employee&mode=employee"
                  className="nav-item"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Bell size={16} />
                  Prenumeratos
                </Link>
                <Link
                  to="/register?type=employee&mode=employee"
                  className="nav-item"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Star size={16} />
                  Pažymėti skelbimai
                </Link>
              </>
            )}
          </div>

          <div className="nav-right">
            {isAuthenticated && user?.user_type === 'employer' ? (
              <Link to="/jobs/create?mode=employer" className="upload-cv-btn">
                <Plus size={14} />
                SUKURTI SKELBIMĄ
              </Link>
            ) : isAuthenticated && (user?.user_type === 'employee' || user?.user_type === 'job_seeker') ? (
              <Link to="/cv/create" className="upload-cv-btn">
                <Plus size={14} />
                Naujas CV
              </Link>
            ) : currentPath === 'employer' ? (
              <Link to="/register?type=employer&mode=employer" className="upload-cv-btn">
                <Plus size={14} />
                SUKURTI SKELBIMĄ
              </Link>
            ) : (
              <Link to="/register?type=employee&mode=employee" className="upload-cv-btn">
                <Upload size={14} />
                ĮKELTI CV
              </Link>
            )}
          </div>
        </div>
      </nav>
      </div>{/* end sticky-header-wrap */}

      {/* MAIN CONTENT */}
      <main className="main-content">
        {children}
      </main>

      {/* FOOTER */}
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span className="footer-copyright">© 2026 CVGIDAS</span>
            <Link to="/atsiliepimai" className="footer-link">Atsiliepimai</Link>
            <Link to="/naudojimosi-salygos" className="footer-link">Naudojimosi sąlygos</Link>
            <Link to="/pagalba" className="footer-link">Pagalba</Link>
            <Link to="/kontaktai" className="footer-link">Susisiekite su mumis</Link>
          </div>
          <div className="footer-right">
            <a 
              href="https://www.facebook.com/cvgidas" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="footer-social"
              aria-label="Facebook"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href="https://www.linkedin.com/company/cvgidas" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="footer-social"
              aria-label="LinkedIn"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </footer>

      {showTrialModal && user && (
        <FreeTrialWelcomeModal
          companyName={user.company_name}
          registeredAt={user.created_at}
          onClose={() => {
            setShowTrialModal(false);
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const token = localStorage.getItem('auth_token');
            if (token) {
              fetch(`${API_URL}/profile/free-trial-welcomed`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
              }).catch(() => { /* non-blocking */ });
            }
          }}
        />
      )}

    </div>
  );
};

export default Layout;
